import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { notificationService } from '../../../services/notificationService';
import { useAuth } from '../../../contexts/AuthContext';

const PREF_GROUPS = [
  {
    label: 'Account',
    items: [
      { key: 'invite_email', label: 'Invitation emails', description: 'Receive an email when you are invited to a site or company' },
      { key: 'password_reset', label: 'Password reset', description: 'Receive password reset emails when requested' },
    ],
  },
  {
    label: 'Documents',
    items: [
      { key: 'document_expiry_30d', label: '30-day expiry warning', description: 'Alert when a document expires within 30 days' },
      { key: 'document_expiry_7d', label: '7-day expiry alert', description: 'Urgent alert when a document expires within 7 days' },
    ],
  },
  {
    label: 'Certifications',
    items: [
      { key: 'cert_expiring', label: 'Certification expiring (30 days)', description: 'Alert when a worker certification is expiring within 30 days' },
      { key: 'cert_expired', label: 'Certification expired', description: 'Alert when a worker certification has expired' },
    ],
  },
  {
    label: 'Incidents',
    items: [
      { key: 'incident_reported', label: 'New incident reported', description: 'Notification when a new incident is reported on site' },
      { key: 'incident_status_changed', label: 'Incident status changed', description: 'Notification when an incident status is updated' },
    ],
  },
  {
    label: 'H&S Docs & Inductions',
    items: [
      { key: 'rams_assigned', label: 'H&S document assigned', description: 'Notification when an H&S document is assigned to you' },
      { key: 'induction_assigned', label: 'Site induction assigned', description: 'Notification when a site induction is assigned to you' },
      { key: 'toolbox_talk_assigned', label: 'Toolbox talk assigned', description: 'Notification when a toolbox talk is assigned to you' },
    ],
  },
  {
    label: 'Snags',
    items: [
      { key: 'snag_assigned', label: 'Snag assigned to me', description: 'Email when a snag is assigned to you' },
      { key: 'snag_overdue', label: 'Overdue snag reminder', description: 'Reminder when an assigned snag is past its due date' },
    ],
  },
  {
    label: 'Tasks',
    items: [
      { key: 'task_assigned', label: 'Task assigned to me', description: 'Email when a task is assigned to you' },
      { key: 'task_overdue', label: 'Overdue task reminder', description: 'Reminder when an assigned task is past its due date' },
    ],
  },
  {
    label: 'Digest',
    items: [
      { key: 'weekly_digest', label: 'Weekly digest summary', description: 'Weekly email with completion rates, open snags, and expiring docs (Admin & Site Manager only)' },
    ],
  },
];

const DEFAULT_PREFS = {
  invite_email: true,
  password_reset: true,
  document_expiry_30d: true,
  document_expiry_7d: true,
  cert_expiring: true,
  cert_expired: true,
  incident_reported: true,
  incident_status_changed: true,
  rams_assigned: true,
  induction_assigned: true,
  toolbox_talk_assigned: true,
  snag_assigned: true,
  snag_overdue: true,
  task_assigned: true,
  task_overdue: true,
  weekly_digest: true,
};

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
      checked ? 'bg-accent' : 'bg-muted'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

const NotificationPreferences = () => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (user?.id) loadPrefs();
  }, [user?.id]);

  const loadPrefs = async () => {
    setLoading(true);
    const { data } = await notificationService?.getPreferences(user?.id);
    if (data) {
      setPrefs(prev => ({ ...prev, ...data }));
    }
    setLoading(false);
  };

  const handleToggle = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    const { error } = await notificationService?.updatePreferences(user?.id, prefs);
    if (error) {
      setSaveError(error?.message || 'Failed to save preferences');
    } else {
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-1 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Bell" size={18} className="text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Email Notifications</h2>
        </div>
        <div className="space-y-3">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="h-10 bg-muted/40 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-1 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="Bell" size={18} className="text-accent" />
          <h2 className="text-lg font-semibold text-foreground">Email Notifications</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-60"
        >
          {saving ? (
            <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Icon name="Save" size={15} />
          )}
          Save
        </button>
      </div>
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg mb-4 text-sm text-success">
          <Icon name="CheckCircle" size={16} />
          Notification preferences saved
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg mb-4 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          {saveError}
        </div>
      )}
      <div className="space-y-6">
        {PREF_GROUPS?.map(group => (
          <div key={group?.label}>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{group?.label}</h3>
            <div className="space-y-3">
              {group?.items?.map(item => (
                <div key={item?.key} className="flex items-center justify-between gap-4 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{item?.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item?.description}</p>
                  </div>
                  <Toggle
                    checked={prefs?.[item?.key] ?? true}
                    onChange={val => handleToggle(item?.key, val)}
                    disabled={saving}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationPreferences;
